import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, ArrowUpRight, Handshake, ClipboardCheck, Send, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REVIEW_CALL_URL =
  "https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ2N746MuptRQGLcn_uXzpK2g7lRVFOimO5X-yIKhMlSjfzxexcJvtN7NHVygr-xD2sqD0XLYYLV?gv=true";

const SERVICE_TYPES = [
  "Payments & acceptance diagnostic",
  "AIOps Partner engagement",
  "Studio Retainer (monthly)",
  "Revenue Audit Lite",
  "Custom project",
  "Not sure — help me scope it",
];

const HOW_IT_WORKS = [
  {
    Icon: Send,
    num: "01",
    title: "You send the referral.",
    body: "Fill in the short form below with the prospect basics and the service you think fits. Two minutes, no login needed.",
  },
  {
    Icon: ClipboardCheck,
    num: "02",
    title: "We review the fit.",
    body: "We qualify the opportunity against active engagements, capacity, and stack fit. Book a 15-minute review call using the scheduler and we'll walk it together.",
  },
  {
    Icon: Handshake,
    num: "03",
    title: "You get paid when it closes.",
    body: "Referral fees are paid on invoiced revenue. Rate and payout schedule confirmed in your Agent agreement before the deal closes — no ambiguity, no clawbacks.",
  },
];

const initialForm = {
  referrer_name: "",
  referrer_email: "",
  referrer_org: "",
  prospect_name: "",
  prospect_url: "",
  hq_city: "",
  hq_state: "",
  hq_country: "",
  service_type: SERVICE_TYPES[0],
  notes: "",
};

export default function Agent() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.referrer_name.trim() || !form.referrer_email.trim() || !form.prospect_name.trim()) {
      toast.error("Your name, email, and the prospect name are required.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/agent/referrals`, form);
      setSubmitted(true);
      toast.success("Referral received. We'll be in touch within two business days.");
    } catch (err) {
      toast.error("Something went wrong. Please email rev@sideb.io and we'll pick it up manually.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white" data-testid="agent-page">
      <Helmet>
        <title>SideB Agent · Refer a deal, get paid on close</title>
        <meta
          name="description"
          content="The SideB Agent program pays a referral fee on qualified opportunities that close. Send us the prospect, book a 15-minute review, and we'll handle the diagnostic from there."
        />
        <meta name="keywords" content="SideB referral, revenue consulting referral program, AI consulting reseller, payments consulting affiliate" />
        <meta property="og:title" content="SideB Agent · Refer a deal, get paid on close" />
        <meta property="og:description" content="Submit a qualified opportunity. Book a 15-minute review. Get paid on invoiced revenue." />
        <meta property="og:url" content="https://sideb.io/agent" />
      </Helmet>

      {/* ---- DARK HERO ---------------------------------------------------- */}
      <section className="bg-[#0a1628] text-white border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.18em] text-[#ac62ea] mb-5" data-testid="agent-eyebrow">
            <span className="w-1.5 h-1.5 rounded-full bg-[#ac62ea]" />
            SideB Agent Program
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.035em] leading-[1.02] max-w-3xl">
            Refer a deal.<br />
            <span className="text-[#ac62ea]">Get paid on close.</span>
          </h1>
          <p className="mt-6 text-base text-white/70 max-w-2xl leading-relaxed">
            The SideB Agent program is a simple referral track for consultants, operators, and agencies who see revenue-performance
            problems in their network that we're built to solve. You send the intro. We handle the diagnostic. You get paid when
            the engagement invoices.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button
              asChild
              className="bg-[#ac62ea] hover:bg-[#9450d6] text-white rounded-full px-6"
              data-testid="agent-hero-review-btn"
            >
              <a href={REVIEW_CALL_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2">
                <CalendarDays className="h-4 w-4" />
                Book a 15-min review
              </a>
            </Button>
            <a
              href="#refer-form"
              className="inline-flex items-center gap-1.5 text-sm text-white/85 hover:text-white transition-colors font-medium"
              data-testid="agent-hero-form-anchor"
            >
              Submit a referral first
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* ---- HOW IT WORKS ------------------------------------------------- */}
      <section className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-foreground/55 mb-10">
            How it works
          </div>
          <div className="grid md:grid-cols-3 gap-8 lg:gap-10">
            {HOW_IT_WORKS.map(({ Icon, num, title, body }) => (
              <div key={num} className="border-t border-border pt-6" data-testid={`agent-step-${num}`}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-[11px] font-mono uppercase tracking-[0.14em] text-[#ac62ea]">{num}</span>
                  <Icon className="h-4 w-4 text-foreground/60" />
                </div>
                <h3 className="font-display text-xl sm:text-2xl font-semibold tracking-[-0.02em] leading-snug text-foreground">
                  {title}
                </h3>
                <p className="mt-3 text-sm text-foreground/65 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---- REFERRAL FORM ------------------------------------------------ */}
      <section id="refer-form" className="bg-[#faf7ff]">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-foreground/55 mb-3">
            Submit a referral
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-[-0.03em] leading-[1.05] mb-3">
            Two minutes. No login.
          </h2>
          <p className="text-sm text-foreground/65 mb-10 max-w-2xl leading-relaxed">
            Give us enough to qualify the opportunity. We'll come back within two business days with a fit assessment and — if the
            match is there — a proposed next step for the prospect.
          </p>

          {submitted ? (
            <div
              className="rounded-2xl border border-[#0a1628]/10 bg-white p-8 lg:p-10"
              data-testid="agent-form-success"
            >
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle2 className="h-6 w-6 text-[#ac62ea]" />
                <h3 className="font-display text-2xl font-semibold tracking-[-0.02em]">Referral received.</h3>
              </div>
              <p className="text-sm text-foreground/70 leading-relaxed mb-6">
                Thanks — we've sent a confirmation to your email and will respond within{" "}
                <strong>72 hours</strong> with availability and next steps. In the meantime,
                the fastest way to move it forward is to book a 15-minute review with us
                and walk the opportunity live.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  asChild
                  className="bg-[#ac62ea] hover:bg-[#9450d6] text-white rounded-full px-6"
                  data-testid="agent-success-book-btn"
                >
                  <a href={REVIEW_CALL_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2">
                    <CalendarDays className="h-4 w-4" />
                    Book a 15-min review
                  </a>
                </Button>
                <Button
                  variant="outline"
                  className="rounded-full"
                  onClick={() => { setSubmitted(false); setForm(initialForm); }}
                  data-testid="agent-success-refer-again-btn"
                >
                  Submit another
                </Button>
              </div>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="rounded-2xl border border-[#0a1628]/10 bg-white p-8 lg:p-10 space-y-6"
              data-testid="agent-referral-form"
            >
              {/* Referrer */}
              <FieldGroup label="About you">
                <div className="grid md:grid-cols-2 gap-4">
                  <TextField label="Your name *" value={form.referrer_name} onChange={update("referrer_name")} testId="agent-referrer-name" required />
                  <TextField label="Your email *" type="email" value={form.referrer_email} onChange={update("referrer_email")} testId="agent-referrer-email" required />
                </div>
                <div className="mt-4">
                  <TextField label="Your organization (optional)" value={form.referrer_org} onChange={update("referrer_org")} testId="agent-referrer-org" />
                </div>
              </FieldGroup>

              {/* Prospect */}
              <FieldGroup label="About the prospect">
                <div className="grid md:grid-cols-2 gap-4">
                  <TextField label="Prospect name *" value={form.prospect_name} onChange={update("prospect_name")} testId="agent-prospect-name" required />
                  <TextField label="Prospect URL" placeholder="https://" value={form.prospect_url} onChange={update("prospect_url")} testId="agent-prospect-url" />
                </div>
                <div className="grid md:grid-cols-3 gap-4 mt-4">
                  <TextField label="HQ city" value={form.hq_city} onChange={update("hq_city")} testId="agent-hq-city" />
                  <TextField label="State / region" value={form.hq_state} onChange={update("hq_state")} testId="agent-hq-state" />
                  <TextField label="Country" value={form.hq_country} onChange={update("hq_country")} testId="agent-hq-country" />
                </div>
              </FieldGroup>

              {/* Deal */}
              <FieldGroup label="Deal shape">
                <SelectField
                  label="Service type"
                  value={form.service_type}
                  onChange={update("service_type")}
                  options={SERVICE_TYPES}
                  testId="agent-service-type"
                />
                <div className="mt-4">
                  <TextArea
                    label="Anything else we should know? (optional)"
                    value={form.notes}
                    onChange={update("notes")}
                    testId="agent-notes"
                    placeholder="Timing, budget signal, decision-maker, current pain point, warm-intro angle…"
                    rows={4}
                  />
                </div>
              </FieldGroup>

              <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-border">
                <p className="text-xs text-foreground/50 max-w-md leading-relaxed">
                  By submitting, you agree to the <Link to="#terms" className="underline hover:text-[#ac62ea]">Agent terms</Link> below.
                  We'll never share your name with the prospect without your consent.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-full"
                    onClick={() => window.open(REVIEW_CALL_URL, "_blank", "noreferrer")}
                    data-testid="agent-form-book-btn"
                  >
                    <CalendarDays className="h-4 w-4 mr-1.5" />
                    Or book a review call
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="bg-[#ac62ea] hover:bg-[#9450d6] text-white rounded-full px-6"
                    data-testid="agent-form-submit-btn"
                  >
                    {submitting ? "Sending…" : "Send referral"}
                  </Button>
                </div>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* ---- TERMS -------------------------------------------------------- */}
      <section id="terms" className="bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-foreground/55 mb-3">
            Agent terms — the short version
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-[-0.03em] leading-[1.05] mb-8">
            Simple. In writing before anything closes.
          </h2>
          <div className="space-y-6 text-sm text-foreground/75 leading-relaxed max-w-3xl">
            <p>
              <strong className="text-foreground">Referral fee is paid on invoiced revenue.</strong> Once a referred prospect signs
              an SOW and pays their first invoice, the referral fee is calculated on collected revenue for the initial engagement
              term. The exact rate and duration are confirmed in a short Agent agreement before the deal closes — nothing is
              retroactive, nothing is ambiguous.
            </p>
            <p>
              <strong className="text-foreground">You own the introduction; we own the sale.</strong> Once we accept a referral,
              we work the opportunity directly with the prospect. You stay in the loop on status but you're not on the hook for the
              scoping, delivery, or account management. If the prospect isn't a fit, we'll tell you promptly so you can pursue other
              angles without ambiguity.
            </p>
            <p>
              <strong className="text-foreground">Attribution window.</strong> A referral is credited to you for 12 months from
              submission. If the same prospect closes with SideB during that window through any channel, the referral fee is yours.
              After 12 months the attribution lapses unless the opportunity is actively in motion.
            </p>
            <p>
              <strong className="text-foreground">Confidentiality goes both ways.</strong> Anything you share about the prospect is
              treated as commercial-in-confidence. We won't disclose your involvement to the prospect without your explicit consent,
              and we won't pursue overlapping engagements you're already running with the same client without a conversation.
            </p>
            <p className="text-xs text-foreground/55 italic">
              This is a plain-English summary. The full Agent agreement — with rate schedule, payment cadence, and standard legal
              provisions — is sent for signature before your first deal closes. Questions?{" "}
              <a href="mailto:rev@sideb.io" className="underline hover:text-[#ac62ea]">rev@sideb.io</a>.
            </p>
          </div>
        </div>
      </section>

      {/* ---- CLOSING CTA -------------------------------------------------- */}
      <section className="bg-[#0a1628] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div>
            <h3 className="font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] leading-[1.1] max-w-2xl">
              Have a live opportunity? Walk it with us on a call.
            </h3>
            <p className="mt-3 text-sm text-white/65 max-w-xl leading-relaxed">
              15 minutes. We'll tell you honestly if it's a fit before you send the intro.
            </p>
          </div>
          <Button
            asChild
            className="bg-[#ac62ea] hover:bg-[#9450d6] text-white rounded-full px-6 whitespace-nowrap"
            data-testid="agent-closing-cta-btn"
          >
            <a href={REVIEW_CALL_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2">
              <CalendarDays className="h-4 w-4" />
              Book a 15-min review
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
}

/* ------------------------------ FIELDS ------------------------------ */

const FieldGroup = ({ label, children }) => (
  <fieldset>
    <legend className="text-[11px] font-mono uppercase tracking-[0.14em] text-foreground/55 mb-4">{label}</legend>
    {children}
  </fieldset>
);

const TextField = ({ label, value, onChange, testId, type = "text", placeholder, required = false }) => (
  <label className="block">
    <span className="block text-xs font-medium text-foreground/70 mb-1.5">{label}</span>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      data-testid={testId}
      className="w-full rounded-lg border border-border bg-white px-3.5 py-2.5 text-sm text-foreground placeholder:text-foreground/40 focus:outline-none focus:ring-2 focus:ring-[#ac62ea]/40 focus:border-[#ac62ea] transition-colors"
    />
  </label>
);

const TextArea = ({ label, value, onChange, testId, placeholder, rows = 3 }) => (
  <label className="block">
    <span className="block text-xs font-medium text-foreground/70 mb-1.5">{label}</span>
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      data-testid={testId}
      className="w-full rounded-lg border border-border bg-white px-3.5 py-2.5 text-sm text-foreground placeholder:text-foreground/40 focus:outline-none focus:ring-2 focus:ring-[#ac62ea]/40 focus:border-[#ac62ea] transition-colors resize-y"
    />
  </label>
);

const SelectField = ({ label, value, onChange, options, testId }) => (
  <label className="block">
    <span className="block text-xs font-medium text-foreground/70 mb-1.5">{label}</span>
    <select
      value={value}
      onChange={onChange}
      data-testid={testId}
      className="w-full rounded-lg border border-border bg-white px-3.5 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-[#ac62ea]/40 focus:border-[#ac62ea] transition-colors"
    >
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  </label>
);
